import React, { useEffect, useState, useCallback } from "react";
import { Col, Row } from "reactstrap";
import { Select, message, Spin, Tag, Button, Upload } from "antd";
import {
  DownloadOutlined,
  UploadOutlined,
  FileDoneOutlined,
  FileSearchOutlined,
  SaveOutlined,
  ReloadOutlined,
  PaperClipOutlined,
} from "@ant-design/icons";
import {
  getTotVendorListApi,
  getTotDocumentApi,
  getBlobDownloadUrlApi,
  uploadBlobApi,
  uploadTotDocumentApi,
} from "../../../services/totaggrtement";

const STATUS_COLOR_MAP = {
  Pending: "orange",
  Uploaded: "green",
  Approved: "blue",
  Rejected: "red",
  "Under Review": "purple",
};

const UploadTOTDocument = () => {
  const [vendors, setVendors] = useState([]);
  const [selectedVendorGUID, setSelectedVendorGUID] = useState(null);
  const [docInfo, setDocInfo] = useState(null);

  // existing document download URL (fetched from blob/download)
  const [existingDownloadUrl, setExistingDownloadUrl] = useState(null);

  // newly chosen file (staged — not yet saved to record)
  const [stagedFileName, setStagedFileName] = useState(null);       // blob filename returned after upload
  const [stagedDownloadUrl, setStagedDownloadUrl] = useState(null); // download URL for staged file

  const [vendorLoading, setVendorLoading] = useState(false);
  const [docLoading, setDocLoading] = useState(false);
  const [blobUploadLoading, setBlobUploadLoading] = useState(false); // uploading to blob
  const [saveLoading, setSaveLoading] = useState(false);             // saving record
  const [existingUrlLoading, setExistingUrlLoading] = useState(false);

  // ── fetch vendor list ────────────────────────────────────────────────────
  useEffect(() => {
    const fetchVendors = async () => {
      try {
        setVendorLoading(true);
        const res = await getTotVendorListApi();
        if (res?.IsSuccess) {
          setVendors(res.Data || []);
        } else {
          message.error(res?.DisplayMessage || "Failed to load vendors");
        }
      } catch (error) {
        message.error(error?.message || "Failed to load vendors");
      } finally {
        setVendorLoading(false);
      }
    };
    fetchVendors();
  }, []);

  // ── fetch blob download URL helper ──────────────────────────────────────
  const fetchDownloadUrl = useCallback(async (fileName, setter, loaderSetter) => {
    try {
      if (loaderSetter) loaderSetter(true);
      const res = await getBlobDownloadUrlApi(fileName);
      if (res?.IsSuccess) {
        setter(res.Data);
      } else {
        message.warning("Could not fetch download link.");
      }
    } catch (error) {
      message.error(error?.message || "Failed to fetch download URL");
    } finally {
      if (loaderSetter) loaderSetter(false);
    }
  }, []);

  // ── fetch TOT document record ────────────────────────────────────────────
  const fetchTotDocument = useCallback(
    async (vendorGUID) => {
      try {
        setDocLoading(true);
        setDocInfo(null);
        setExistingDownloadUrl(null);
        clearStaged();

        const res = await getTotDocumentApi(vendorGUID);
        if (res?.IsSuccess && res.Data?.length > 0) {
          const record = res.Data[0];
          setDocInfo(record);
          if (record.TOTAgreement) {
            fetchDownloadUrl(record.TOTAgreement, setExistingDownloadUrl, setExistingUrlLoading);
          }
        }
        // no warning if empty — we still show the upload panel
      } catch (error) {
        message.error(error?.message || "Failed to fetch TOT document info");
      } finally {
        setDocLoading(false);
      }
    },
    [fetchDownloadUrl]
  );

  // ── vendor select / clear ────────────────────────────────────────────────
  const handleVendorChange = (value) => {
    setSelectedVendorGUID(value ?? null);
    if (value) {
      fetchTotDocument(value);
    } else {
      setDocInfo(null);
      setExistingDownloadUrl(null);
      clearStaged();
    }
  };

  // ── clear staged file state ──────────────────────────────────────────────
  const clearStaged = () => {
    setStagedFileName(null);
    setStagedDownloadUrl(null);
  };

  // ── Step 1: pick file → upload to blob → stage result ───────────────────
  const handleFileChoose = async ({ file }) => {
    if (!selectedVendorGUID) {
      message.warning("Please select a vendor first.");
      return;
    }
    try {
      setBlobUploadLoading(true);
      clearStaged();

      const blobRes = await uploadBlobApi(file);
      if (!blobRes?.IsSuccess) {
        message.error(blobRes?.DisplayMessage || "File upload to blob failed");
        return;
      }

      const uploadedFileName = blobRes.Data;
      setStagedFileName(uploadedFileName);

      // fetch a download URL for the staged file so user can preview/verify
      await fetchDownloadUrl(uploadedFileName, setStagedDownloadUrl, null);

      message.success("File ready. Click Save to confirm.");
    } catch (error) {
      message.error(error?.message || "File upload failed");
    } finally {
      setBlobUploadLoading(false);
    }
  };

  // ── Step 2: Save → persist the staged file to the vendor record ──────────
  const handleSave = async () => {
    if (!stagedFileName) {
      message.warning("Please choose a file first.");
      return;
    }
    try {
      setSaveLoading(true);
      const saveRes = await uploadTotDocumentApi(selectedVendorGUID, stagedFileName);
      if (saveRes?.IsSuccess) {
        handleVendorChange(null);
      }
      // success / error messages handled inside uploadTotDocumentApi
    } catch (error) {
      message.error(error?.message || "Save failed");
    } finally {
      setSaveLoading(false);
    }
  };

  // ── Reset → clear staged file ────────────────────────────────────────────
  const handleReset = () => {
    handleVendorChange(null);
  };

  // ── render ───────────────────────────────────────────────────────────────
  return (
    <div style={{ padding: "1rem" }}>
      {/* Header */}
      <Row style={{ marginBottom: "1.25rem", alignItems: "center" }}>
        <Col>
          <h5 style={{ fontWeight: 600, color: "#1a1a2e", marginBottom: 0, display: "flex", alignItems: "center", gap: 8 }}>
            <FileDoneOutlined style={{ color: "#1890ff" }} />
            Upload TOT Document
          </h5>
        </Col>
      </Row>

      {/* Vendor dropdown */}
      <Row style={{ marginBottom: "1rem" }}>
        <Col xl={5} md={8} sm={12}>
          <label style={{ fontWeight: 500, display: "block", marginBottom: 4 }}>
            Select Vendor <span style={{ color: "red" }}>*</span>
          </label>
          <Select
            placeholder="Search and select vendor..."
            allowClear
            showSearch
            loading={vendorLoading}
            optionFilterProp="label"
            style={{ width: "100%" }}
            size="middle"
            value={selectedVendorGUID}
            onChange={handleVendorChange}
            options={vendors.map((v) => ({
              label: v.VendorName
                ? `${v.VendorName} — ${v.VendorType}`
                : `${v.VendorGUID} (${v.VendorType})`,
              value: v.VendorGUID,
            }))}
          />
        </Col>
      </Row>

      {/* Spinner while loading doc record */}
      {docLoading && (
        <Row style={{ marginTop: "2rem" }}>
          <Col style={{ textAlign: "center" }}>
            <Spin size="large" tip="Loading document info..." />
          </Col>
        </Row>
      )}

      {/* Empty state — no vendor selected */}
      {!docLoading && !selectedVendorGUID && (
        <div style={{ textAlign: "center", padding: "2.5rem", color: "#aaa", background: "#fafafa", borderRadius: 8, border: "1px dashed #d9d9d9", marginTop: "0.5rem" }}>
          <FileSearchOutlined style={{ fontSize: 36, marginBottom: 8 }} />
          <div style={{ fontSize: 14 }}>Select a vendor to view TOT document details.</div>
        </div>
      )}

      {/* Document panel — shown once a vendor is selected */}
      {!docLoading && selectedVendorGUID && (
        <div style={{ background: "#f8faff", border: "1px solid #d6e4ff", borderRadius: 8, padding: "1.25rem 1.5rem", marginTop: "0.25rem" }}>

          {/* Row 1: Upload Status + existing document link */}
          <Row style={{ alignItems: "center", marginBottom: "1.25rem" }}>

            {/* Upload Status — always visible when record exists */}
            {docInfo && (
              <Col xl={4} md={6} sm={12} style={{ marginBottom: "0.5rem" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontWeight: 600, color: "#444" }}>Upload Status:</span>
                  <Tag
                    color={STATUS_COLOR_MAP[docInfo.UploadStatus] ?? "default"}
                    style={{ fontSize: 13, padding: "2px 12px", borderRadius: 20 }}
                  >
                    {docInfo.UploadStatus || "—"}
                  </Tag>
                </div>
              </Col>
            )}

            {/* Existing saved document — always visible */}
            {docInfo && (
              <Col xl={6} md={6} sm={12} style={{ marginBottom: "0.5rem" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontWeight: 600, color: "#444" }}>Current Document:</span>
                  {existingUrlLoading ? (
                    <Spin size="small" />
                  ) : docInfo.TOTAgreement && existingDownloadUrl ? (
                    <a
                      href={existingDownloadUrl}
                      target="_blank"
                      rel="noreferrer"
                      download={docInfo.TOTAgreement}
                      style={{ display: "inline-flex", alignItems: "center", gap: 4, color: "#1890ff", fontSize: 13 }}
                    >
                      <DownloadOutlined />
                      {docInfo.TOTAgreement}
                    </a>
                  ) : docInfo.TOTAgreement && !existingDownloadUrl ? (
                    <span style={{ color: "#aaa", fontSize: 13 }}>Link unavailable</span>
                  ) : (
                    <span style={{ color: "#aaa", fontSize: 13 }}>No file uploaded yet</span>
                  )}
                </div>
              </Col>
            )}

            {/* No record found indicator */}
            {!docInfo && (
              <Col style={{ marginBottom: "0.5rem" }}>
                <Tag color="orange" style={{ fontSize: 13, padding: "2px 12px", borderRadius: 20 }}>
                  No document record found
                </Tag>
              </Col>
            )}
          </Row>

          {/* Row 2: File picker */}
          <Row style={{ alignItems: "flex-end", marginBottom: "1rem" }}>
            <Col xl={6} md={8} sm={12}>
              <div style={{ fontWeight: 600, color: "#444", marginBottom: 8 }}>
                {docInfo?.TOTAgreement ? "Replace Document:" : "Upload Document:"}
              </div>

              <Upload
                customRequest={handleFileChoose}
                showUploadList={false}
                accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                disabled={blobUploadLoading || saveLoading}
                maxCount={1}
              >
                <Button
                  icon={<UploadOutlined />}
                  loading={blobUploadLoading}
                  disabled={saveLoading}
                  style={{ borderColor: "#1890ff", color: "#1890ff" }}
                >
                  {blobUploadLoading ? "Uploading..." : "Choose File"}
                </Button>
              </Upload>

              <div style={{ color: "#888", fontSize: 12, marginTop: 6 }}>
                Accepted: PDF, DOC, DOCX, PNG, JPG
              </div>
            </Col>
          </Row>

          {/* Row 3: Staged file preview — download anchor link */}
          {stagedFileName && (
            <Row style={{ marginBottom: "1rem" }}>
              <Col>
                <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "#e6f7ff", border: "1px solid #91d5ff", borderRadius: 6, padding: "6px 14px" }}>
                  <PaperClipOutlined style={{ color: "#1890ff" }} />
                  {stagedDownloadUrl ? (
                    <a
                      href={stagedDownloadUrl}
                      target="_blank"
                      rel="noreferrer"
                      download={stagedFileName}
                      style={{ color: "#1890ff", fontSize: 13 }}
                    >
                      {stagedFileName}
                    </a>
                  ) : (
                    <span style={{ fontSize: 13, color: "#555" }}>{stagedFileName}</span>
                  )}
                  <span style={{ fontSize: 11, color: "#888", marginLeft: 4 }}>— Ready to save</span>
                </div>
              </Col>
            </Row>
          )}

          {/* Row 4: Save & Reset buttons */}
          {selectedVendorGUID && (
            <Row>
              <Col>
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  loading={saveLoading}
                  disabled={!stagedFileName || blobUploadLoading}
                  onClick={handleSave}
                  style={{
                    marginRight: "0.75rem",
                    backgroundColor: "#52c41a",
                    borderColor: "#52c41a",
                    boxShadow: "0 2px 6px rgba(82,196,26,0.3)",
                  }}
                >
                  Save
                </Button>

                <Button
                  icon={<ReloadOutlined />}
                  disabled={!stagedFileName || saveLoading}
                  onClick={handleReset}
                  style={{ borderColor: "#ff4d4f", color: "#ff4d4f" }}
                >
                  Reset
                </Button>
              </Col>
            </Row>
          )}
        </div>
      )}
    </div>
  );
};

export default UploadTOTDocument;